<!-- resources/js/frontend/pages/Privacy.vue -->
<template>
  <div class="container py-10 mx-auto px-1 sm:px-0">
    <!-- Header -->
    <header class="mb-6 pb-4 border-b border-neutral-200">
      <div class="uppercase tracking-wider text-neutral-500 font-semibold text-xs">Storm Count Exchange</div>
      <h1 class="text-3xl sm:text-4xl font-bold mt-1">Privacy Policy</h1>

      <div class="flex items-center gap-2 mt-2">
        <span class="inline-block w-2 h-2 rounded-full bg-sky-400 shadow-[0_0_12px_rgba(56,189,248,0.8)]"></span>
        <p class="text-neutral-600">A division of Perfect Storm Comics and Games</p>
      </div>

      <div class="flex flex-wrap gap-2 mt-3 text-sm text-neutral-700">
        <span class="border border-neutral-200 rounded-full px-3 py-1 bg-neutral-50">
          Effective Date: <strong>{{ effectiveDate }}</strong>
        </span>
        <span class="border border-neutral-200 rounded-full px-3 py-1 bg-neutral-50">
          Contact:
          <!-- <a href="mailto:privacy@perfectstormcomics.com" class="text-sky-600 underline">privacy@perfectstormcomics.com</a> -->
        </span>
      </div>
    </header>

    <!-- TOC -->
    <nav class="flex flex-wrap gap-2 my-4" aria-label="Table of Contents">
      <a href="#information-we-collect" class="border border-neutral-200 px-3 py-1.5 rounded-full bg-white hover:bg-neutral-50">Information We Collect</a>
      <a href="#how-we-use" class="border border-neutral-200 px-3 py-1.5 rounded-full bg-white hover:bg-neutral-50">How We Use Information</a>
      <a href="#cookies" class="border border-neutral-200 px-3 py-1.5 rounded-full bg-white hover:bg-neutral-50">Cookies &amp; Analytics</a>
      <a href="#sharing" class="border border-neutral-200 px-3 py-1.5 rounded-full bg-white hover:bg-neutral-50">Data Sharing &amp; Disclosure</a>
      <a href="#security" class="border border-neutral-200 px-3 py-1.5 rounded-full bg-white hover:bg-neutral-50">Data Security</a>
      <a href="#your-rights" class="border border-neutral-200 px-3 py-1.5 rounded-full bg-white hover:bg-neutral-50">Your Rights</a>
      <a href="#updates" class="border border-neutral-200 px-3 py-1.5 rounded-full bg-white hover:bg-neutral-50">Updates to This Policy</a>
      <a href="#contact" class="border border-neutral-200 px-3 py-1.5 rounded-full bg-white hover:bg-neutral-50">Contact Us</a>
      <a href="#in-short" class="border border-neutral-200 px-3 py-1.5 rounded-full bg-white hover:bg-neutral-50">In Short</a> <!-- NEW -->
    </nav>

    <!-- Card -->
    <main class="bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm space-y-8" role="main">
      <!-- Intro -->
      <section id="intro" class="text-neutral-800">
        <p>
          <!-- UPDATED -->
          At Storm Count Exchange, transparency matters as much as trust. We collect only what’s necessary to keep the gears
          turning — to identify, evaluate, and complete your exchanges safely and efficiently. This policy outlines what
          information we collect, how we use it, and the principles that guide how we handle your data.
        </p>
      </section>

      <!-- 1. Information We Collect -->
      <section id="information-we-collect" class="text-neutral-800">
        <h2 class="text-xl font-semibold mb-2">1. Information We Collect</h2>
        <p>When you browse our site, submit a collection, or finalize a sale, we may collect:</p>
        <ul class="list-disc pl-6 mt-2 space-y-1">
          <li><strong>Contact information</strong> (such as your name, email address, mailing address, and phone number).</li>
          <li><strong>Payment &amp; transaction information</strong> necessary to process exchanges or payouts.</li>
          <li><strong>User-submitted details</strong> including collection details, card lists, or notes you provide.</li>
          <li><strong>Technical data</strong> such as device and browser type, device information, IP address, and cookies used for analytics and site performance.</li>
        </ul>
        <p class="mt-2">
          <!-- UPDATED -->
          We collect this data directly from you, and we do not purchase or share user data from third parties.
        </p>
      </section>

      <!-- 2. How We Use -->
      <section id="how-we-use" class="text-neutral-800">
        <h2 class="text-xl font-semibold mb-2">2. How We Use Your Information</h2>
        <p>Your data helps us provide a smoother, safer, and more accurate exchange process. Specifically, we use it to:</p>
        <ul class="list-disc pl-6 mt-2 space-y-1">
          <li>Verify and complete your submissions and payments.</li>
          <li>Communicate about offers, updates, or issues with your exchange.</li>
          <li>Improve the performance and experience of our website and services.</li>
          <li>Maintain records required for security, accounting, or legal compliance.</li>
        </ul>
        <p class="text-emerald-600 font-semibold mt-2">We do not sell or rent your personal information — ever.</p>
      </section>

      <!-- 3. Cookies -->
      <section id="cookies" class="text-neutral-800">
        <h2 class="text-xl font-semibold mb-2">3. Cookies &amp; Analytics</h2>
        <p>
          <!-- UPDATED -->
          We use limited cookies and analytics tools to understand traffic patterns and site performance. These are used
          strictly to improve user experience — not to track individuals across the internet or build marketing profiles.
        </p>
        <p class="mt-2">
          <!-- UPDATED -->
          You can adjust your browser settings to refuse cookies or alert you when they’re in use, though some site features may not
          function optimally without them.
        </p>
      </section>

      <!-- 4. Sharing -->
      <section id="sharing" class="text-neutral-800">
        <h2 class="text-xl font-semibold mb-2">4. Data Sharing &amp; Disclosure</h2>
        <p>
          <!-- UPDATED -->
          We keep your information within our own system. Storm Count Exchange does not partner with third-party data
          processors or analytics services that access your personal data. We will only share information when required by
          law or to protect the integrity and safety of our customers and our business.
        </p>
      </section>

      <!-- 5. Security -->
      <section id="security" class="text-neutral-800">
        <h2 class="text-xl font-semibold mb-2">5. Data Security</h2>
        <p>
          <!-- UPDATED -->
          We take security seriously. Your information is protected using reasonable administrative, technical, and physical safeguards
          designed to prevent unauthorized access, misuse, or disclosure. While no system is entirely immune to risk, we continuously
          monitor and update our practices to stay ahead of potential threats.
        </p>
      </section>

      <!-- 6. Your Rights -->
      <section id="your-rights" class="text-neutral-800">
        <h2 class="text-xl font-semibold mb-2">6. Your Rights</h2>
        <p>You have the right to:</p>
        <ul class="list-disc pl-6 mt-2 space-y-1">
          <li>Request access to your personal data.</li>
          <li>Request correction or deletion of your information.</li>
          <li>Withdraw consent for communications at any time.</li>
        </ul>
        <p class="mt-2">
          <!-- UPDATED -->
          To make a request, please contact us at:
          <!-- Uncomment when ready -->
          <!-- <a href="mailto:privacy@perfectstormcomics.com" class="text-sky-600 underline">privacy@perfectstormcomics.com</a> -->
        </p>
      </section>

      <!-- 7. Updates -->
      <section id="updates" class="text-neutral-800">
        <h2 class="text-xl font-semibold mb-2">7. Updates to This Policy</h2>
        <p>
          We may update this policy periodically. Any changes will be posted on this page with an updated effective date.
          We encourage you to review it occasionally to stay informed.
        </p>
      </section>

      <!-- 8. Contact -->
      <section id="contact" class="text-neutral-800">
        <h2 class="text-xl font-semibold mb-2">8. Contact Us</h2>
        <address class="not-italic">
          Perfect Storm Comics and Games<br>
          Attn: Privacy Officer<br>
          669 Capital Ave SW<br>
          Battle Creek, MI 49015<br>
          Email:
          <!-- Uncomment when we're ready to launch this e-mail address -->
          <!-- <a href="mailto:privacy@perfectstormcomics.com" class="text-sky-600 underline">privacy@perfectstormcomics.com</a> -->
        </address>
      </section>

      <!-- 9. In Short (new) -->
      <section id="in-short" class="text-neutral-800">
        <h2 class="text-xl font-semibold mb-2">In Short</h2>
        <p>
          We respect your privacy, we protect your information, and we use it only to make the exchange process as smooth
          and rewarding as possible — just like every transaction should be.
        </p>
      </section>
    </main>

    <!-- Footer note -->
    <footer class="mt-6 text-sm text-neutral-600">
      <p>&copy; {{ year }} Perfect Storm Comics and Games. Storm Count Exchange is a division of Perfect Storm Comics and Games.</p>
    </footer>
  </div>
</template>

<script setup>
const year = new Date().getFullYear()
// Set this to whatever you want displayed in the header “Effective Date”
const effectiveDate = 'October 20, 2025'
</script>
